<template>
  <main-large-dialog
    :is-visible.sync="computedIsVisible"
    :is-valid="true"
    :loading="computedIsLoading"
    v-bind="computedDialogProps"
  >
    <template #body>
      <v-col cols="12" v-if="isFetched && searchFetched">
        <v-row no-gutters justify="end">
          <v-col cols="3" class="pr-5 py-4">
            <data-input v-model="searchValue" v-bind="searchInputBind" />
          </v-col>
        </v-row>
        <v-row no-gutters style="overflow-x: auto; max-height: 55vh">
          <v-col cols="12">
            <v-row no-gutters class="flex-nowrap column-dialog-headers" align="center">
              <v-col
                v-for="(column, idx) of columns"
                :key="idx"
                class="border-top border-bottom cell-height background-white"
                :style="getCellWidth(column)"
              >
                <v-row no-gutters align="center" justify="center" class="cell-height">
                  <v-col cols="auto" class="font-weight-medium pr-1">
                    {{ column[columnVerboseNameKey] }}
                  </v-col>
                  <v-col cols="auto" v-if="column.optional" class="text-caption font-weight-thin font-italic">
                    {{ $i18n.t('dialog.body.optional') }}
                  </v-col>
                </v-row>
              </v-col>
              <v-col
                class="d-flex justify-center align-center font-weight-medium columns-dialog-header-options border-top border-bottom cell-height"
              >
                {{ $i18n.t('dialog.body.options') }}
              </v-col>
            </v-row>
            <v-form ref="form-row-new" v-model="isNewRowValid">
              <v-row no-gutters class="flex-nowrap" align="center">
                <v-col
                  v-for="(column, idx) of columns"
                  :key="idx"
                  class="border-bottom cell-height background-white"
                  :style="getCellWidth(column)"
                >
                  <v-row no-gutters align="center" justify="center" class="cell-height">
                    <v-col cols="12" class="px-7" v-if="column.dataInput">
                      <data-input v-bind="column.dataInput" v-model="newRow[column[columnNameKeyPath]]" />
                    </v-col>
                    <v-col cols="auto" class="pr-1" v-else> </v-col>
                  </v-row>
                </v-col>
                <v-col class="columns-dialog-header-options border-bottom cell-height">
                  <v-row no-gutters align="center" justify="center" class="cell-height">
                    <v-col cols="12">
                      <v-tooltip color="font" bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" color="success" :disabled="!isNewRowValid" @click="onRowAdd(newRow)">
                            mdi-check
                          </v-icon>
                        </template>
                        <span> {{ $i18n.t('dialog.agreeAdd') }} </span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-form>
            <v-form ref="form-rows">
              <v-row no-gutters class="flex-nowrap" align="center" v-for="(row, idx) of rowsFiltered" :key="idx">
                <v-col
                  v-for="(column, idx) of columns"
                  :key="idx"
                  class="border-bottom cell-height background-white"
                  :style="getCellWidth(column)"
                >
                  <v-row no-gutters align="center" justify="center" class="cell-height">
                    <v-col cols="12" class="px-7" v-if="column.dataInput">
                      <data-input
                        v-bind="column.dataInput"
                        :value="getNestedValue(row, column[columnNameKeyPath])"
                        @change="onValueChange($event, column, row)"
                      />
                    </v-col>
                    <v-col cols="auto" class="pr-1" v-else>
                      {{ getNestedValue(row, column[columnNameKeyPath]) }}
                    </v-col>
                  </v-row>
                </v-col>
                <v-col class="columns-dialog-header-options border-bottom cell-height">
                  <v-row no-gutters align="center" justify="center" class="cell-height">
                    <v-col cols="12">
                      <v-tooltip color="font" bottom>
                        <template v-slot:activator="{ on }">
                          <v-icon v-on="on" color="error" @click="onRowDelete(row)">
                            mdi-close
                          </v-icon>
                        </template>
                        <span> {{ $i18n.t('menu.delete') }} </span>
                      </v-tooltip>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-form>
          </v-col>
        </v-row>
      </v-col>
      <v-col v-else align-self="center">
        <main-loader />
      </v-col>
    </template>
  </main-large-dialog>
</template>
<script>
import { get as lodashGet } from 'lodash';
import { debounce } from 'lodash';

import DataInput from '@/components/DataInput';
import MainLargeDialog from '@/components/MainLargeDialog';
import MainLoader from '@/components/MainLoader';

export default {
  name: 'MainColumnsDialog',
  components: { DataInput, MainLargeDialog, MainLoader },
  props: {
    dialogProps: {
      type: Object,
      default: () => {
        return {};
      },
    },
    columnNameKeyPath: {
      type: String,
      default: 'name',
    },
    columnVerboseNameKey: {
      type: String,
      default: 'verboseName',
    },
    columns: {
      type: Array,
      default: () => {
        return [];
      },
    },
    rows: {
      type: Array,
      default: () => {
        return [];
      },
    },
    isVisible: {
      type: Boolean,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isFetched: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    computedIsLoading: {
      get() {
        return this.loading;
      },
      set(nV) {
        this.$emit('update:loading', nV);
      },
    },
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        if (!nV) this.searchFetched = false;
        this.$emit('update:isVisible', nV);
      },
    },
    computedDialogProps() {
      return { ...this.defaultDialogProps, ...this.dialogProps };
    },
    searchInputBind() {
      return {
        dataType: {
          name: 'text',
        },
        prependInnerIcon: 'mdi-magnify',
        placeholder: this.$i18n.t('default.find'),
        hideDetails: true,
        clearable: true,
      };
    },
  },
  data: () => ({
    defaultDialogProps: {
      minBodyHeight: '55vh',
      dialogWidth: '80%',
      headerShadow: true,
      noPaddingsX: true,
      noPaddingsY: true,
      title: '',
    },
    isNewRowValid: false,
    newRow: {},
    searchValue: '',
    searchFetched: false,
    rowsFiltered: [],
  }),
  methods: {
    getCellWidth(column) {
      const width = column.columnWidth || '250px';
      return `width: ${width}; min-width: ${width}`;
    },
    getNestedValue(object, key) {
      return lodashGet(object, key);
    },
    onValueChange(value, column, row) {
      this.$emit('valueChange', { column, row, value });
    },
    onRowDelete(row) {
      this.$emit('rowDelete', row);
    },
    onRowAdd(row = this.newRow) {
      this.$emit('rowAdd', row);
    },
    onSearch(value, isEmpty = false) {
      if (!isEmpty) this.searchFetched = true;
      if (!value) {
        this.rowsFiltered = JSON.parse(JSON.stringify(this.rows));
        this.$refs['form-rows']?.resetValidation();
        return;
      }
      const filterColumns = this.columns.filter(column => column.filter);
      this.rowsFiltered = JSON.parse(JSON.stringify(this.rows)).filter(row => {
        return filterColumns.findIndex(column => {
          let rowValue = this.getNestedValue(row, column[this.columnNameKeyPath]);
          if (column.dataInput?.dataType?.name === 'select') {
            const textKey = column.dataInput.itemText || 'text';
            const valueKey = column.dataInput.itemValue || 'value';
            rowValue = column.dataInput.items.find(i => i[valueKey] === rowValue)?.[textKey];
          }
          return rowValue
            ?.toLocaleString()
            ?.toLocaleLowerCase()
            .includes(value.toLocaleLowerCase());
        }) > -1
          ? true
          : false;
      });
      this.$refs['form-rows']?.resetValidation();
    },
  },
  mounted() {
    this.$watch(
      vm => {
        return { rows: vm.rows, searchValue: vm.searchValue };
      },
      nV => {
        const { searchValue, rows } = nV;
        const isEmpty = !searchValue && !rows?.length && !this.isVisible;
        this.onSearch(searchValue, isEmpty);
      },
      {
        deep: true,
        immediate: true,
      }
    );
  },
  created() {
    this.onSearch = debounce(this.onSearch, 500);
  },
};
</script>
<style lang="scss" scoped>
.cell-height {
  min-height: 68px;
  max-height: 68px;
}
.background-white {
  background-color: #fff;
}
.column-dialog-headers {
  position: sticky;
  top: 0;
  z-index: 1;
}
.columns-dialog-header-options {
  position: sticky;
  right: 0;
  min-width: 80px !important;
  max-width: 80px !important;
  background-color: #fff;
  @include border(left, 1px);
}
.border-top {
  @include border(top, 1px);
}
.border-bottom {
  @include border(bottom, 1px);
}
</style>
